import React from 'react';
import { Clock, Users } from 'lucide-react';
import LazyImage from './LazyImage';
import type { AvailabilitySlot } from './OpenTableAvailability';

const timeValue = (value: string): number => {
  const match = value.trim().match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
  if (!match) return Number.MAX_SAFE_INTEGER;
  let hours = Number(match[1]);
  const period = match[3]?.toUpperCase();
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  return hours * 60 + Number(match[2]);
};

const dateLabel = (date: string) => new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', {
  weekday: 'long', day: 'numeric', month: 'long',
});

const RestaurantAvailabilityCards: React.FC<{ slots: AvailabilitySlot[] }> = ({ slots }) => {
  const byDate = new Map<string, Map<number, AvailabilitySlot[]>>();
  slots.forEach(slot => {
    if (!byDate.has(slot.date)) byDate.set(slot.date, new Map());
    const restaurants = byDate.get(slot.date)!;
    restaurants.set(slot.venue_id, [...(restaurants.get(slot.venue_id) || []), slot]);
  });

  return <section aria-label="Availability by restaurant" className="space-y-7">
    {Array.from(byDate.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([date, restaurants]) => <section key={date}>
      <div className="flex items-center gap-4 mb-4">
        <h2 className="font-display text-2xl text-ink-900"><time dateTime={date}>{dateLabel(date)}</time></h2>
        <div className="flex-grow border-t border-ink-200" />
      </div>
      <div className="restaurant-card-scroller flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 sm:grid sm:grid-cols-2 sm:overflow-visible lg:grid-cols-3 xl:grid-cols-4">
        {Array.from(restaurants.entries()).sort(([, a], [, b]) => a[0].restaurant.localeCompare(b[0].restaurant)).map(([venueId, options]) => {
          const partySizes = Array.from(new Set(options.map(slot => slot.party_size))).sort((a, b) => a - b);
          return <article key={venueId} data-pro-tour="cards" className="restaurant-availability-card flex-none w-[82vw] max-w-sm snap-start overflow-hidden rounded-xl border border-ink-100 bg-surface shadow-sm sm:w-auto sm:max-w-none">
            <LazyImage
              src={options[0].proxied_image || options[0].image || '/default_lazy_restaurant.png'}
              alt={options[0].restaurant}
              className="h-40 w-full object-cover sm:h-48"
              containerClassName="h-40 bg-ink-100 sm:h-48"
            />
            <div className="p-4">
              <h3 className="font-display text-xl text-ink-900">{options[0].restaurant}</h3>
              {partySizes.map(partySize => <div key={partySize} className="mt-3">
                <div className="mb-2 flex items-center text-sm text-ink-500"><Users className="mr-1.5 h-4 w-4" />Table for {partySize}</div>
                <div className="flex gap-2 overflow-x-auto pb-1 custom-scrollbar">
                  {options.filter(slot => slot.party_size === partySize).sort((a, b) => timeValue(a.available_time) - timeValue(b.available_time)).map(slot => <a
                    key={`${slot.party_size}/${slot.available_time}`} data-pro-tour="booking" href={slot.booking_url} target="_blank" rel="noopener noreferrer"
                    aria-label={`Book ${slot.restaurant} for ${slot.party_size} at ${slot.available_time}`}
                    className="inline-flex flex-none items-center rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-800 hover:bg-brand-100">
                    <Clock className="mr-1.5 h-4 w-4" />{slot.available_time}
                  </a>)}
                </div>
              </div>)}
            </div>
          </article>;
        })}
      </div>
    </section>)}
  </section>;
};

export default RestaurantAvailabilityCards;
